import React, { useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import ServiceMatrixContext from '@totem/components/portfolio/serviceMatrix/ServiceMatrixContext';
import { ServiceMatrixRow } from '@totem/components/portfolio/serviceMatrix/types';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { buildingDisplayName } from '@totem/components/ui_components/constants';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';

const styles = {
  salesOrderLink: {
    display: 'block',
    whiteSpace: 'nowrap' as const,
  },
};

const sortText = (left: string, right: string) =>
  (left ?? '').localeCompare(right ?? '', undefined, { sensitivity: 'base' });

const ServiceMatrixTable = () => {
  const { userProfile } = useContext(UserProfileContext);
  const { loading, columns, rows, searchText } =
    useContext(ServiceMatrixContext);
  const [pageSize, setPageSize] = useState<number>(25);

  const canViewSalesOrders =
    permissions.contractsViewer.hasPermissions(userProfile);

  const filteredRows = useMemo(() => {
    if (!searchText) {
      return rows;
    }
    const lower = searchText.toLowerCase();
    return rows.filter(
      (row) =>
        (row.building.name ?? '').toLowerCase().includes(lower) ||
        (row.region.name ?? '').toLowerCase().includes(lower),
    );
  }, [rows, searchText]);

  const regionFilters = useMemo(() => {
    const names = new Set<string>();
    rows.forEach((row) => names.add(row.region.name ?? ''));
    return Array.from(names)
      .sort(sortText)
      .map((name) => ({ text: name || '(No Region)', value: name }));
  }, [rows]);

  const tableColumns = useMemo((): ColumnProps<ServiceMatrixRow>[] => {
    const serviceColumns: ColumnProps<ServiceMatrixRow>[] = columns.map(
      (column) => {
        const buildingCount = rows.filter(
          (row) => (row.cells[column.key] ?? []).length > 0,
        ).length;
        return {
          title: (
            <Tooltip title={column.services.join(', ')}>
              {column.name} ({buildingCount})
            </Tooltip>
          ),
          key: column.key,
          align: 'center',
          filters: [
            { text: 'Active', value: 'active' },
            { text: 'Not Active', value: 'none' },
          ],
          onFilter: (value, record) =>
            (record.cells[column.key] ?? []).length > 0 ===
            (value === 'active'),
          render: (_, record: ServiceMatrixRow) =>
            (record.cells[column.key] ?? []).map((salesOrder) =>
              canViewSalesOrders ? (
                <Link
                  key={salesOrder.salesOrderNumber}
                  style={styles.salesOrderLink}
                  to={`/dashboard/salesOrder/${encodeURIComponent(salesOrder.salesOrderNumber)}`}
                >
                  {salesOrder.salesOrderNumber}
                </Link>
              ) : (
                <span
                  key={salesOrder.salesOrderNumber}
                  style={styles.salesOrderLink}
                >
                  {salesOrder.salesOrderNumber}
                </span>
              ),
            ),
        };
      },
    );

    return [
      {
        title: buildingDisplayName,
        key: 'building',
        fixed: 'left',
        width: 260,
        defaultSortOrder: 'ascend',
        sorter: (left, right) =>
          sortText(left.building.name, right.building.name),
        render: (_, record: ServiceMatrixRow) =>
          record.building.id ? (
            <Link to={`/dashboard/buildings/${record.building.id}`}>
              {record.building.name}
            </Link>
          ) : (
            record.building.name
          ),
      },
      {
        title: 'Region',
        key: 'region',
        width: 180,
        sorter: (left, right) => sortText(left.region.name, right.region.name),
        filters: regionFilters,
        onFilter: (value, record) => (record.region.name ?? '') === value,
        render: (_, record: ServiceMatrixRow) => record.region.name,
      },
      ...serviceColumns,
    ];
  }, [columns, rows, regionFilters, canViewSalesOrders]);

  return (
    <IBTable
      loading={loading}
      columns={tableColumns}
      dataSource={filteredRows}
      rowKey={(record) => record.building.id || record.building.name}
      scroll={{ x: 'max-content' }}
      pagination={{
        pageSize,
        showSizeChanger: true,
        onShowSizeChange: (_, size) => setPageSize(size),
      }}
    />
  );
};

export default ServiceMatrixTable;
